"use client"

import { cn } from "@/utils/helpers"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cva, VariantProps } from "class-variance-authority"
import { ClassValue, StringToBoolean } from "class-variance-authority/types"
import * as React from "react"

type TabsVariantProps = {
  variant: {
    default: string
    underline: string
    pill: string
    outline: string
    blank: string
  }
}

type ConfigSchema = Record<string, Record<string, ClassValue>>
type ConfigVariants<T extends ConfigSchema> = {
  [Variant in keyof T]?: StringToBoolean<keyof T[Variant]> | null | undefined
}
type b = ConfigVariants<TabsVariantProps>

const tabsVariants = cva("", {
  variants: {
    variant: {
      default:
        "text-md-medium group whitespace-nowrap px-8 py-3.5 transition-colors ease-out duration-200 disabled:pointer-events-none disabled:opacity-50 border-b-3 hover:border-primary/30 active:bg-on-surface/12 border-surface/0 data-[state=active]:border-primary text-on-surface-variant data-[state=active]:text-primary flex items-center justify-center gap-2 flex-1",
      underline:
        "text-md-medium group whitespace-nowrap px-8 py-3.5 transition-colors ease-out duration-200 disabled:pointer-events-none disabled:opacity-50 border-b-2 border-transparent data-[state=active]:border-primary flex items-center justify-center gap-2",
      pill: "text-md-medium group whitespace-nowrap px-4 py-2 h-10 transition-colors ease-out duration-200 disabled:pointer-events-none disabled:opacity-50 rounded data-[state=active]:bg-primary-container text-on-surface-variant data-[state=active]:text-on-primary-container hover:bg-on-secondary-container/10 flex items-center justify-center gap-2 flex-1",
      outline:
        "text-md-medium group whitespace-nowrap px-4 py-2 transition-colors ease-out duration-200 disabled:pointer-events-none disabled:opacity-50 border border-surface rounded-md data-[state=active]:border-primary data-[state=active]:text-primary",
      blank: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

// Create a context to store the variant
const TabsVariantContext =
  React.createContext<keyof TabsVariantProps["variant"]>("default")

const Tabs = ({
  variant = "default",
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> &
  VariantProps<typeof tabsVariants>) => {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root className={cn(className)} {...props} />
    </TabsVariantContext.Provider>
  )
}

const tabsListVariants = cva("inline-flex", {
  variants: {
    variant: {
      default: "",
      underline: "",
      pill: "bg-container-low p-1 rounded-lg",
      outline: "",
      blank: "",
    },
  },
  defaultVariants: {
    variant: "default",
  },
})

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext)
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext)

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      tabIndex={0}
      className={cn(tabsVariants({ variant }), className)}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn("data-[state=inactive]:hidden", className)}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsContent, TabsList, TabsTrigger }
